import React, { forwardRef, useLayoutEffect, useRef } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { SearchingAnimation, StaticSearching } from '../../Icons';

interface AdornmentInputProps {
  value: string;
  onFocus: () => void;
  fixedAdornment?: React.ReactNode;
  startAdornments?: React.ReactNode[];
  placeholder?: string;
  className?: string;
  isFocused?: boolean;
  disabled?: boolean;
  onChange: (text: string) => void;
  multiline?: boolean;
  maxRows?: number;
}

const AdornmentInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  AdornmentInputProps
>(
  (
    {
      value = '',
      className = '',
      placeholder,
      startAdornments,
      onFocus,
      isFocused,
      fixedAdornment,
      disabled = false,
      onChange,
      multiline = false,
      maxRows = 6,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
      null,
    );

    const setInputRef = (
      element: HTMLInputElement | HTMLTextAreaElement | null,
    ) => {
      inputRef.current = element;

      if (!ref) {
        return;
      }

      if (typeof ref === 'function') {
        ref(element);
        return;
      }

      ref.current = element;
    };

    useLayoutEffect(() => {
      if (!multiline || !(inputRef.current instanceof HTMLTextAreaElement)) {
        return;
      }

      const textarea = inputRef.current;
      textarea.style.height = 'auto';

      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
      const borderTopWidth = parseFloat(computedStyle.borderTopWidth) || 0;
      const borderBottomWidth =
        parseFloat(computedStyle.borderBottomWidth) || 0;
      const maxHeight =
        lineHeight * maxRows +
        paddingTop +
        paddingBottom +
        borderTopWidth +
        borderBottomWidth;

      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
      textarea.style.overflowY =
        textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [maxRows, multiline, value]);

    const renderControl = (controlClassName: string) =>
      multiline ? (
        <Form.Control
          ref={setInputRef}
          value={value}
          as="textarea"
          rows={1}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={controlClassName}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Form.Control
          ref={setInputRef}
          value={value}
          as="input"
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={controlClassName}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    return (
      <div
        className={`d-flex ${
          multiline ? 'align-items-start' : 'align-items-center'
        } ${className}`}
      >
        <span className="input-fixed-adornment">
          {fixedAdornment ??
            (isFocused ? <SearchingAnimation /> : <StaticSearching />)}
        </span>
        {startAdornments && startAdornments.length > 0 ? (
          <InputGroup.Text className="input-start-adornment">
            {startAdornments}
            {renderControl('input-main')}
          </InputGroup.Text>
        ) : (
          renderControl('input-full')
        )}
      </div>
    );
  },
);

export default AdornmentInput;
